import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios"
import { BACKEND_BASE_URL } from "@/constants/paths/shared"
import { getAppLocale } from "@/i18n"
import { useAuthStore } from "@/stores/auth"

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const REQUEST_TIMEOUT_MS = 30_000

const client = axios.create({
  baseURL: BACKEND_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
})

client.interceptors.request.use((config) => {
  config.headers = config.headers ?? {}
  config.headers["Accept-Language"] = getAppLocale()

  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const refreshClient = axios.create({
  baseURL: BACKEND_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
})

let isRefreshing = false
let refreshQueue: Array<(token: string | null) => void> = []

const resolveQueue = (token: string | null) => {
  refreshQueue.forEach((callback) => callback(token))
  refreshQueue = []
}

function getErrorMessage(error: AxiosError): string {
  const data = error.response?.data
  if (typeof data === "object" && data && "message" in data) {
    const message = (data as { message?: unknown }).message
    if (typeof message === "string") return message
  }
  return ""
}

/** Role/permission failures return 401 from RoleGuard — not an expired session. */
function isAuthorizationFailure(error: AxiosError): boolean {
  const status = error.response?.status
  if (status === 403) return true
  if (status !== 401) return false

  const message = getErrorMessage(error).toLowerCase()
  return (
    message.includes("does not have role") ||
    message.includes("does not have roles") ||
    message.includes("not allowed to manage this business") ||
    message.includes("forbidden")
  )
}

function extractAccessToken(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null
  const record = payload as Record<string, unknown>
  const direct =
    record.accessToken ?? record.token ?? null
  if (typeof direct === "string" && direct) return direct
  const nested = record.data
  if (nested && typeof nested === "object") {
    const nestedRecord = nested as Record<string, unknown>
    const token = nestedRecord.accessToken ?? nestedRecord.token
    if (typeof token === "string" && token) return token
  }
  return null
}

function extractRefreshToken(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null
  const record = payload as Record<string, unknown>
  const direct = record.refreshToken
  if (typeof direct === "string" && direct) return direct
  const nested = record.data
  if (nested && typeof nested === "object") {
    const token = (nested as Record<string, unknown>).refreshToken
    if (typeof token === "string" && token) return token
  }
  return null
}

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status
    const originalRequest = error.config as RetriableConfig | undefined

    if (isAuthorizationFailure(error)) {
      return Promise.reject(error)
    }

    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error)
    }

    originalRequest._retry = true
    const state = useAuthStore.getState()
    const refreshToken = state.refreshToken

    if (!refreshToken) {
      state.logout()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push((newToken) => {
          if (!newToken) {
            reject(error)
            return
          }
          originalRequest.headers = originalRequest.headers ?? {}
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          resolve(client(originalRequest))
        })
      })
    }

    isRefreshing = true
    try {
      const refreshResponse = await refreshClient.post(
        '/api/v1/auth/refreshToken',
        { refreshToken },
      )
      const refreshData = refreshResponse.data ?? {}
      const accessToken = extractAccessToken(refreshData)
      const nextRefreshToken = extractRefreshToken(refreshData) ?? refreshToken

      if (!accessToken) {
        state.logout()
        resolveQueue(null)
        return Promise.reject(error)
      }

      state.setCredentials({
        accessToken,
        refreshToken: nextRefreshToken,
        user: state.user,
      })
      resolveQueue(accessToken)
      originalRequest.headers = originalRequest.headers ?? {}
      originalRequest.headers.Authorization = `Bearer ${accessToken}`
      return client(originalRequest)
    } catch (refreshError) {
      state.logout()
      resolveQueue(null)
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default client
